import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useCurrency } from '../context/CurrencyContext';
import { ChevronRight, Search } from 'lucide-react';

function CurrencySelector() {
  const { currency, setCurrency, supportedCurrencies, getCurrencySymbol } = useCurrency();
  const [isOpen, setIsOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const selectorRef = useRef(null);
  const panelRef = useRef(null);
  const hoverTimeoutRef = useRef(null);
  const mouseMoveHandlerRef = useRef(null);
  const proximityZoneRef = useRef(null);

  // Filter currencies based on search query
  const filteredCurrencies = useMemo(() => {
    if (!searchQuery.trim()) {
      return supportedCurrencies;
    }
    const query = searchQuery.toLowerCase().trim();
    return supportedCurrencies.filter(curr => 
      curr.code.toLowerCase().includes(query) ||
      curr.name.toLowerCase().includes(query)
    );
  }, [searchQuery, supportedCurrencies]);

  // Handle hover to open panel - opens when cursor moves towards the button from the right
  useEffect(() => {
    mouseMoveHandlerRef.current = (e) => {
      if (!selectorRef.current) return;
      const rect = selectorRef.current.getBoundingClientRect();
      const mouseX = e.clientX;
      const mouseY = e.clientY;
      const buttonRight = rect.right;
      const buttonTop = rect.top;
      const buttonBottom = rect.bottom;
      
      // Create a proximity zone: 100px to the right of the button, vertically aligned
      const proximityZoneRight = buttonRight + 100;
      const proximityZoneLeft = buttonRight - 20; // Start detecting 20px before button
      const proximityZoneTop = buttonTop - 50; // Extend 50px above
      const proximityZoneBottom = buttonBottom + 50; // Extend 50px below
      
      // Check if cursor is in the proximity zone (moving towards button from right)
      if (
        mouseX >= proximityZoneLeft && 
        mouseX <= proximityZoneRight &&
        mouseY >= proximityZoneTop && 
        mouseY <= proximityZoneBottom
      ) {
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
        }
        setIsHovering(true);
      }
    };

    // Add global mousemove listener to detect cursor approaching from right
    document.addEventListener('mousemove', mouseMoveHandlerRef.current);
    
    return () => {
      if (mouseMoveHandlerRef.current) {
        document.removeEventListener('mousemove', mouseMoveHandlerRef.current);
      }
    };
  }, []);

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        selectorRef.current && 
        !selectorRef.current.contains(event.target) &&
        panelRef.current &&
        !panelRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setIsHovering(false);
      }
    };
    
    if (isOpen || isHovering) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, isHovering]);

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      if (!isOpen) {
        setIsHovering(false);
      }
    }, 300); // Delay to allow moving to panel
  };

  const handlePanelMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setIsHovering(true);
  };

  const handlePanelMouseLeave = () => {
    setIsHovering(false);
    if (!isOpen) {
      setIsHovering(false);
    }
  };

  const handleCurrencyChange = (newCurrency) => {
    if (newCurrency !== currency) {
      setCurrency(newCurrency);
    }
    setIsOpen(false);
    setIsHovering(false);
  };

  const togglePanel = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setIsHovering(true);
    } else {
      setSearchQuery(''); // Clear search when closing
    }
  };

  return (
    <>
      <div 
        ref={selectorRef}
        className="currency-selector-wrapper"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <button
          className="currency-selector-btn"
          onClick={togglePanel}
          aria-label="Select currency"
        >
          <span className="currency-code">{currency}</span>
        </button>
      </div>

      {/* Slide-in Panel */}
      <div
        ref={panelRef}
        className={`currency-panel ${isOpen || isHovering ? 'open' : ''}`}
        onMouseEnter={handlePanelMouseEnter}
        onMouseLeave={handlePanelMouseLeave}
      >
        <div className="currency-panel-header">
          <h3 className="currency-panel-title">SELECT YOUR CURRENCY</h3>
          <div className="currency-search-container">
            <Search className="currency-search-icon" size={16} />
            <input
              type="text"
              className="currency-search-input"
              placeholder="Search currency..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus={isOpen || isHovering}
            />
          </div>
        </div>
        <div className="currency-panel-content">
          {filteredCurrencies.length > 0 ? (
            filteredCurrencies.map((curr) => (
            <button
              key={curr.code}
              className={`currency-panel-option ${currency === curr.code ? 'active' : ''}`}
              onClick={() => handleCurrencyChange(curr.code)}
            >
              {currency === curr.code && (
                <ChevronRight className="currency-option-indicator" size={16} />
              )}
              <span className="currency-option-code">{curr.code}</span>
              <span className="currency-option-name">{curr.name}</span>
            </button>
            ))
          ) : (
            <div className="currency-no-results">
              <p>No currencies found</p>
              <span>Try a different search term</span>
            </div>
          )}
        </div>
      </div>

      {/* Overlay when panel is open */}
      {(isOpen || isHovering) && (
        <div 
          className="currency-panel-overlay"
          onClick={() => {
            setIsOpen(false);
            setIsHovering(false);
          }}
        />
      )}

      <style>{`
        .currency-selector-wrapper {
          position: fixed;
          top: 50%;
          right: 0;
          transform: translateY(-50%);
          z-index: 1001;
          margin-right: 0;
        }

        .currency-selector-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          background: #171717;
          color: #ffffff !important;
          border: none;
          // border-radius: 4px 0 0 4px;
          padding: 8px 8px;
          cursor: pointer;
          font-size: 13px;
          font-weight: 500;
          letter-spacing: 0.5px;
          transition: all 0.2s ease;
          min-width: 40px;
          box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15);
        }

        .currency-selector-btn:hover {
          background: #2a2a2a;
        }

        .currency-code {
          font-weight: 500;
          color: #ffffff !important;
        }

        .currency-panel {
          position: fixed;
          top: 0;
          right: -400px;
          width: 380px;
          height: 100vh;
          background: #2a2a2a;
          box-shadow: -4px 0 20px rgba(0, 0, 0, 0.3);
          z-index: 1002;
          transition: right 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .currency-panel.open {
          right: 0;
        }

        .currency-panel-header {
          padding: 32px 24px 24px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .currency-panel-title {
          color: #ffffff;
          font-size: 14px;
          font-weight: 600;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          margin: 0 0 16px 0;
        }

        .currency-search-container {
          position: relative;
          display: flex;
          align-items: center;
          margin-top: 16px;
        }

        .currency-search-icon {
          position: absolute;
          left: 12px;
          color: rgba(255, 255, 255, 0.5);
          pointer-events: none;
        }

        .currency-search-input {
          width: 100%;
          padding: 10px 12px 10px 36px;
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 6px;
          color: #ffffff;
          font-size: 14px;
          outline: none;
          transition: all 0.2s ease;
        }

        .currency-search-input::placeholder {
          color: rgba(255, 255, 255, 0.4);
        }

        .currency-search-input:focus {
          background: rgba(255, 255, 255, 0.15);
          border-color: rgba(255, 255, 255, 0.4);
        }

        .currency-no-results {
          padding: 40px 24px;
          text-align: center;
          color: rgba(255, 255, 255, 0.6);
        }

        .currency-no-results p {
          margin: 0 0 8px 0;
          font-size: 16px;
          font-weight: 500;
        }

        .currency-no-results span {
          font-size: 13px;
          color: rgba(255, 255, 255, 0.4);
        }

        .currency-panel-content {
          flex: 1;
          overflow-y: auto;
          padding: 8px 0;
        }

        .currency-panel-content::-webkit-scrollbar {
          width: 6px;
        }

        .currency-panel-content::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
        }

        .currency-panel-content::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.2);
          border-radius: 3px;
        }

        .currency-panel-content::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.3);
        }

        .currency-panel-option {
          display: flex;
          align-items: center;
          width: 100%;
          padding: 14px 24px;
          background: transparent;
          border: none;
          color: #ffffff;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.15s ease;
          text-align: left;
          position: relative;
        }

        .currency-panel-option:hover {
          background: rgba(255, 255, 255, 0.08);
        }

        .currency-panel-option.active {
          background: rgba(255, 255, 255, 0.12);
        }

        .currency-option-indicator {
          position: absolute;
          left: 8px;
          color: #ffffff;
          opacity: 0.9;
        }

        .currency-option-code {
          font-weight: 500;
          min-width: 50px;
          margin-left: 24px;
          color: #ffffff;
        }

        .currency-panel-option.active .currency-option-code {
          margin-left: 0;
        }

        .currency-option-name {
          flex: 1;
          color: rgba(255, 255, 255, 0.9);
          font-weight: 400;
          text-transform: uppercase;
          font-size: 12px;
          letter-spacing: 0.5px;
        }

        .currency-panel-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.3);
          z-index: 1001;
          opacity: 0;
          animation: fadeIn 0.2s ease forwards;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        /* Mobile responsiveness */
        @media (max-width: 768px) {
          .currency-panel {
            width: 100%;
            right: -100%;
          }

          .currency-panel.open {
            right: 0;
          }
        }
      `}</style>
    </>
  );
}

export default CurrencySelector;
